import type { AuctisyncSupabase } from './client.js';
import { parsePhotoEdit, type PhotoEdit } from './photo-edit-description.js';
import { DataRequestError } from './request-error.js';

/**
 * Staff photo editing — reading a lot's photos with their ids, putting them in
 * order, and deleting one. The office website and the Cataloging app both go
 * through here, so the two cannot disagree about what "move" or "delete" does.
 *
 * The public, read-only side is `lot-photos.ts`; it deliberately returns URLs
 * only. Editing needs the ids.
 *
 * 🔴 THE FIRST PHOTO IS THE COVER. Every listing surface shows
 * `sort_order` 0 when it has room for one picture, so an order is not cosmetic.
 */

/** One photo as staff edit it. */
export interface StaffPhoto {
  id: string;
  /**
   * The id the uploading device minted (Cataloging app), or null for a photo
   * uploaded from the website. A tablet that took the photo offline knows it
   * by this id before it knows the real one.
   */
  clientUploadId: string | null;
  /** The photo everyone sees — the edited render once it has been edited. */
  url: string;
  sortOrder: number;
  /**
   * The photo as it was taken — what the editor edits. Equal to `url` until
   * the first edit. 🔴 Never edit `url`: once a photo is edited that is the
   * render, and editing it again would crop the crop.
   */
  originalUrl: string;
  /** Its edits as a description of the original, or null when it is as taken. */
  edit: PhotoEdit | null;
  /** The background cut-out, while the background is removed. */
  maskUrl: string | null;
}

interface StaffPhotoRow {
  id: string;
  client_upload_id: string | null;
  display_url: string | null;
  sort_order: number | null;
}

interface PhotoEditRow {
  id: string;
  edit: unknown;
  original_url: string | null;
  mask_url: string | null;
}

/**
 * A lot's own photos, in order, each with its edits. Ties (older rows share
 * `sort_order` 0) break by upload time, which is the order they were taken.
 *
 * Two reads in parallel: the photos themselves, and their edits, which are
 * staff-only columns read through `photo_edits_staff` (20261001120000).
 * 🔴 Either failing fails the whole read. A photo list without its edits would
 * offer the RENDER to the editor as if it were the original.
 */
export async function fetchStaffLotPhotos(
  supabase: AuctisyncSupabase,
  lotId: string,
): Promise<StaffPhoto[]> {
  const [photos, edits] = await Promise.all([
    supabase
      .from('inventory_photos')
      .select('id, client_upload_id, display_url, sort_order')
      .eq('lot_id', lotId)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true }),
    supabase
      .from('photo_edits_staff')
      .select('id, edit, original_url, mask_url')
      .eq('lot_id', lotId),
  ]);
  if (photos.error) throw new DataRequestError(photos.error);
  if (edits.error) throw new DataRequestError(edits.error);
  const editsById = new Map<string, PhotoEditRow>();
  for (const e of (edits.data ?? []) as PhotoEditRow[]) editsById.set(e.id, e);

  const out: StaffPhoto[] = [];
  for (const r of (photos.data ?? []) as StaffPhotoRow[]) {
    const url = r.display_url?.trim();
    if (!url) continue;
    const e = editsById.get(r.id);
    const edit = e ? parsePhotoEdit(e.edit) : null;
    out.push({
      id: r.id,
      clientUploadId: r.client_upload_id,
      url,
      sortOrder: r.sort_order ?? 0,
      originalUrl: e?.original_url?.trim() || url,
      edit,
      maskUrl: edit?.bg ? e?.mask_url?.trim() || null : null,
    });
  }
  return out;
}

/** Whose photos: a lot's, or an inventory item's that is not on a lot. */
export type PhotoOwner = { lotId: string } | { inventoryItemId: string };

/**
 * Put an owner's photos in this order, in one server call
 * (`public.reorder_photos`). Each entry is a photo id or a `client_upload_id`.
 *
 * Photos the list leaves out keep their old order behind the named ones, and a
 * name that matches nothing is ignored — so a reorder that arrives after a new
 * photo, or after a delete, never loses anything.
 *
 * Throws `DataRequestError`: a refusal carries a Postgres code (`42501` not
 * staff, `P0002` no such lot), a dropped connection does not.
 */
export async function reorderPhotos(
  supabase: AuctisyncSupabase,
  owner: PhotoOwner,
  order: readonly string[],
): Promise<void> {
  const { error } = await supabase.rpc('reorder_photos', {
    p_order: [...order],
    ...('lotId' in owner
      ? { p_lot_id: owner.lotId }
      : { p_inventory_item_id: owner.inventoryItemId }),
  });
  if (error) throw new DataRequestError(error);
}

/** A photo, by its real id or by the id the device minted for it. */
export type PhotoRef = { photoId: string } | { clientUploadId: string };

export type DeletePhotoResult =
  | {
      ok: true;
      /** The photo was not there — an earlier attempt already deleted it. */
      alreadyGone: boolean;
    }
  | {
      ok: false;
      /** HTTP status, or 0 when the request never got an answer. */
      status: number;
      error: string;
    };

/**
 * Delete a photo — the row and the stored file — through the
 * `delete-inventory-photo` Edge Function.
 *
 * "Not found" is SUCCESS here. A delete whose first attempt landed but whose
 * answer was lost (warehouse wifi) is retried, and the retry finds nothing;
 * that is the delete having worked, not failed.
 */
export async function deletePhoto(
  supabase: AuctisyncSupabase,
  ref: PhotoRef,
): Promise<DeletePhotoResult> {
  const body =
    'photoId' in ref ? { photo_id: ref.photoId } : { client_upload_id: ref.clientUploadId };
  const { data, error } = (await supabase.functions.invoke('delete-inventory-photo', {
    body,
  })) as { data: unknown; error: unknown };
  if (!error) {
    const deleted = (data as { deleted?: unknown } | null)?.deleted === true;
    return deleted
      ? { ok: true, alreadyGone: false }
      : { ok: false, status: 200, error: 'unexpected_response' };
  }
  const { status, code } = await httpFailure(error);
  if (status === 404 && code === 'photo_not_found') return { ok: true, alreadyGone: true };
  return {
    ok: false,
    status,
    error: code ?? (status === 0 ? 'no_response' : `http_${String(status)}`),
  };
}

/**
 * What an Edge Function call's error says. A FunctionsHttpError carries the
 * raw Response on `context`; a network failure carries none, which is status 0.
 */
async function httpFailure(error: unknown): Promise<{ status: number; code: string | null }> {
  const context: unknown = (error as { context?: unknown } | null)?.context;
  if (!isResponseLike(context)) return { status: 0, code: null };
  const res = context as Response;
  let code: string | null = null;
  try {
    const parsed: unknown = await res.json();
    const e = (parsed as { error?: unknown } | null)?.error;
    if (typeof e === 'string' && e !== '') code = e;
  } catch {
    // Not JSON — the status says enough.
  }
  return { status: res.status, code };
}

function isResponseLike(v: unknown): boolean {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as { status?: unknown }).status === 'number' &&
    typeof (v as { json?: unknown }).json === 'function'
  );
}

/**
 * The list with one entry moved from `from` to `to`. Out-of-range indexes
 * return the list unchanged — a double-tap on "move left" at the start of the
 * strip is a no-op, not an error.
 */
export function moveItem<T>(list: readonly T[], from: number, to: number): T[] {
  if (from < 0 || from >= list.length || to < 0 || to >= list.length || from === to) {
    return [...list];
  }
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item as T);
  return next;
}
